/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useDevice } from '@squiz/dxp-console-support';
import React, { useEffect, useRef } from 'react';

export const SequenceListDots = ({
  index,
  dependencies,
}: {
  index: number;
  dependencies: Array<unknown>;
}): JSX.Element => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const device = useDevice();
  // Canvas testing is very limited, so ignore this function.
  // istanbul ignore next
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    requestAnimationFrame(() => {
      // Get the position of the two elements that the dots will be drawn between.
      const indexPositionA = document
        .querySelector(`.sds-sequence-list__item-index-mark[data-index="${index + 1}"]`)
        ?.getBoundingClientRect();
      const indexPositionB = document
        .querySelector(`.sds-sequence-list__item-index-mark[data-index="${index + 2}"]`)
        ?.getBoundingClientRect();
      // If two elements are not found, do not draw the dots.
      if (!indexPositionB || !indexPositionA) {
        return;
      }

      const height = indexPositionB.top - indexPositionA.bottom;
      const dotSize = 4;
      // Minimum spacing between dots is 12, maximum 16. Try to find an optimal number of dots to fit the height whilst having satisfied the spacing.
      let dotSpacing = 12;
      let minRemainder = height % dotSpacing;
      for (let spacing = 13; spacing <= 16; spacing++) {
        const currentRemainder = height % spacing;
        if (currentRemainder < minRemainder) {
          minRemainder = currentRemainder;
          dotSpacing = spacing;
        }
      }
      const maxDots = Math.floor(height / dotSpacing);

      // Set the canvas size and position to start from the bottom of upper element and end at the top of the lower element (in combination with CSS).
      canvas.style.height = `calc(${height}px - 12px)`;
      canvas.style.top = `calc(50% + ${indexPositionA.height / 2}px + 6px)`;
      canvas.width = canvas.offsetWidth;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }
      // Draw dots
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#f5bb8c';

      for (let i = 0; i < maxDots; i++) {
        const y = i * dotSpacing + dotSpacing / 2;
        ctx.beginPath();
        ctx.arc(canvas.width / 2, y, dotSize / 2, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
    // Clear canvas on unmount.
    return (): void => canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  }, [dependencies, device, index]);

  return <canvas className="sds-sequence-list__item-dots" ref={canvasRef} />;
};
